import { Link, useLocation } from '@tanstack/react-router'
import { Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/custom/sheet'
import { LanguageSwitch } from '@/components/language-switch'
import { ThemeToggle } from '@/components/theme-toggle'
import { useTrackEvent } from '@/lib/posthog/use-track-event'

const HEADER_LABELS = {
  de: {
    features: 'Funktionen',
    materials: 'Anleitungen',
    login: 'Anmelden',
    openIqdoc: 'IQDOC öffnen',
    startFree: 'Kostenlos starten',
    openMenu: 'Menü öffnen',
    closeMenu: 'Menü schließen',
    mobileNavigation: 'Mobile Navigation',
    switchToLight: 'Zum hellen Design wechseln',
    switchToDark: 'Zum dunklen Design wechseln',
    themeToggle: 'Design umschalten',
    skipToContent: 'Zum Inhalt springen',
  },
  en: {
    features: 'Features',
    materials: 'Guides',
    login: 'Sign in',
    openIqdoc: 'Open IQDOC',
    startFree: 'Start free',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    mobileNavigation: 'Mobile navigation',
    switchToLight: 'Switch to light theme',
    switchToDark: 'Switch to dark theme',
    themeToggle: 'Toggle theme',
    skipToContent: 'Skip to content',
  },
  es: {
    features: 'Funciones',
    materials: 'Guías',
    login: 'Iniciar sesión',
    openIqdoc: 'Abrir IQDOC',
    startFree: 'Empezar gratis',
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    mobileNavigation: 'Navegación móvil',
    switchToLight: 'Cambiar al tema claro',
    switchToDark: 'Cambiar al tema oscuro',
    themeToggle: 'Cambiar tema',
    skipToContent: 'Saltar al contenido',
  },
  pt: {
    features: 'Recursos',
    materials: 'Guias',
    login: 'Entrar',
    openIqdoc: 'Abrir IQDOC',
    startFree: 'Começar grátis',
    openMenu: 'Abrir menu',
    closeMenu: 'Fechar menu',
    mobileNavigation: 'Navegação móvel',
    switchToLight: 'Mudar para o tema claro',
    switchToDark: 'Mudar para o tema escuro',
    themeToggle: 'Alternar tema',
    skipToContent: 'Ir para o conteúdo',
  },
  ru: {
    features: 'Возможности',
    materials: 'Материалы',
    login: 'Войти',
    openIqdoc: 'Открыть IQDOC',
    startFree: 'Начать бесплатно',
    openMenu: 'Открыть меню',
    closeMenu: 'Закрыть меню',
    mobileNavigation: 'Мобильная навигация',
    switchToLight: 'Включить светлую тему',
    switchToDark: 'Включить темную тему',
    themeToggle: 'Переключить тему',
    skipToContent: 'Перейти к содержанию',
  },
  uz: {
    features: 'Imkoniyatlar',
    materials: 'Qo‘llanmalar',
    login: 'Kirish',
    openIqdoc: 'IQDOC’ni ochish',
    startFree: 'Bepul boshlash',
    openMenu: 'Menyuni ochish',
    closeMenu: 'Menyuni yopish',
    mobileNavigation: 'Mobil navigatsiya',
    switchToLight: 'Yorug‘ mavzuni yoqish',
    switchToDark: 'Qorong‘i mavzuni yoqish',
    themeToggle: 'Mavzuni almashtirish',
    skipToContent: 'Asosiy mazmunga o‘tish',
  },
} as const

function getHeaderLabels(language: string) {
  switch (language.split('-').at(0)) {
    case 'de':
      return HEADER_LABELS.de
    case 'es':
      return HEADER_LABELS.es
    case 'pt':
      return HEADER_LABELS.pt
    case 'ru':
      return HEADER_LABELS.ru
    case 'uz':
      return HEADER_LABELS.uz
    default:
      return HEADER_LABELS.en
  }
}

export function LandingHeader() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const trackEvent = useTrackEvent()
  const { t, i18n } = useTranslation()
  const labels = getHeaderLabels(i18n.language)
  const { hash, pathname } = useLocation()
  const navigationItems = [
    {
      href: '/#how-it-works',
      label: labels.features,
      trackingValue: 'features',
    },
    {
      href: '/research',
      label: t('LANDING_NAV_RESEARCH'),
      trackingValue: 'research',
    },
    {
      href: '/posts',
      label: labels.materials,
      trackingValue: 'materials',
    },
    {
      href: '/calculators',
      label: t('LANDING_NAV_CALCULATORS'),
      trackingValue: 'calculators',
    },
    {
      href: '/pricing',
      label: t('LANDING_NAV_PRICING'),
      trackingValue: 'pricing',
    },
    {
      href: '/about',
      label: t('LANDING_NAV_ABOUT'),
      trackingValue: 'about',
    },
  ]

  const getNavigationItemCurrent = (href: string) => {
    if (href.startsWith('/#')) {
      return pathname === '/' && hash === href.slice(1) ? 'location' : undefined
    }
    return pathname === href || pathname.startsWith(`${href}/`)
      ? 'page'
      : undefined
  }

  useEffect(() => {
    let isActive = true

    const loadSession = async () => {
      try {
        const response = await fetch('/api/auth/get-session', {
          credentials: 'include',
        })
        const session = response.ok
          ? ((await response.json()) as { user?: unknown })
          : null
        if (isActive) setIsAuthenticated(Boolean(session?.user))
      } catch {
        if (isActive) setIsAuthenticated(false)
      }
    }

    loadSession().catch(() => undefined)
    return () => {
      isActive = false
    }
  }, [])

  const trackNavigation = (link: string, placement: 'desktop' | 'mobile') => {
    trackEvent('landing_nav_click', { link, placement })
  }

  const trackChatCta = (placement: 'desktop' | 'mobile') => {
    trackEvent('landing_cta_click', {
      type: 'start_chat',
      authenticated: isAuthenticated === true,
      placement,
    })
  }

  return (
    <header className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-border bg-background/85 px-4 py-3 text-sm tracking-wide backdrop-blur-xl sm:px-6 sm:py-4 md:px-10 lg:px-16">
      <a
        className="fixed top-3 left-4 z-[70] -translate-y-24 rounded-lg bg-background px-4 py-3 font-semibold text-foreground shadow-lg transition-transform focus:translate-y-0 focus:ring-2 focus:ring-ring focus:outline-none"
        href="#main-content"
      >
        {labels.skipToContent}
      </a>
      <Link className="flex min-h-11 shrink-0 items-center gap-2" to="/">
        <img
          alt=""
          className="h-8 w-auto dark:hidden"
          src="/Logo_landing.svg"
        />
        <img
          alt=""
          className="hidden h-8 w-auto dark:block"
          src="/Logo_landing_inverted.svg"
        />
        <span
          className="text-xl font-extrabold tracking-tight text-foreground"
          style={{
            fontFamily: 'Manrope, sans-serif',
            letterSpacing: '-0.04em',
          }}
        >
          IQDOC
        </span>
      </Link>

      <nav
        aria-label={t('LANDING_NAV_ARIA')}
        className="hidden items-center gap-4 text-[13px] text-foreground min-[1180px]:flex 2xl:gap-7 2xl:text-sm"
      >
        {navigationItems.map((item) => {
          const ariaCurrent = getNavigationItemCurrent(item.href)
          return (
            <a
              aria-current={ariaCurrent}
              className={`inline-flex min-h-11 items-center border-b-2 pt-0.5 whitespace-nowrap transition-colors focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary ${
                ariaCurrent
                  ? 'border-primary text-foreground'
                  : 'border-transparent hover:text-foreground/70'
              }`}
              href={item.href}
              key={item.href}
              onClick={() => trackNavigation(item.trackingValue, 'desktop')}
            >
              {item.label}
            </a>
          )
        })}
      </nav>

      <div className="flex shrink-0 items-center gap-2 text-sm sm:gap-3">
        <LanguageSwitch />
        <ThemeToggle
          labels={{
            switchToDark: labels.switchToDark,
            switchToLight: labels.switchToLight,
            toggle: labels.themeToggle,
          }}
        />

        {isAuthenticated === null ? (
          <div
            aria-hidden="true"
            className="hidden h-11 w-44 min-[1180px]:block"
          />
        ) : null}

        {isAuthenticated === false && (
          <>
            <Link
              className="hidden min-h-11 items-center rounded-xl px-3 text-sm font-semibold whitespace-nowrap text-foreground transition-colors hover:text-foreground/70 min-[1180px]:inline-flex"
              onClick={() => trackEvent('landing_cta_click', { type: 'login' })}
              search={{ redirect: '/chat' }}
              to="/login"
            >
              {labels.login}
            </Link>
            <Link
              className="hidden min-h-11 items-center rounded-xl bg-primary px-4 text-sm font-semibold whitespace-nowrap text-primary-foreground shadow-md transition-transform hover:brightness-110 active:scale-95 min-[1180px]:inline-flex"
              onClick={() => trackChatCta('desktop')}
              to="/chat"
            >
              {labels.startFree}
            </Link>
          </>
        )}
        {isAuthenticated === true && (
          <Link
            className="hidden min-h-11 items-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-md transition-transform hover:brightness-110 active:scale-95 min-[1180px]:inline-flex"
            onClick={() => trackChatCta('desktop')}
            to="/chat"
          >
            {labels.openIqdoc}
          </Link>
        )}

        <Sheet>
          <SheetTrigger asChild>
            <button
              aria-label={labels.openMenu}
              className="inline-flex size-11 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:border-[#A94300] hover:text-[#A94300] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#A94300] min-[1180px]:hidden dark:hover:border-primary/60 dark:hover:text-[#FF9B4A] dark:focus-visible:outline-primary"
              type="button"
            >
              <Menu className="size-5" />
            </button>
          </SheetTrigger>
          <SheetContent
            aria-describedby={undefined}
            className="flex h-dvh w-[min(88vw,24rem)] flex-col overflow-y-auto border-border bg-background p-6"
            hideClose={true}
            side="right"
          >
            <div className="flex items-center justify-between border-b border-border pb-5">
              <SheetTitle className="text-base font-bold tracking-tight">
                IQDOC
              </SheetTitle>
              <SheetClose asChild>
                <button
                  aria-label={labels.closeMenu}
                  className="inline-flex size-11 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:border-[#A94300] hover:text-[#A94300] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#A94300] dark:hover:border-primary/60 dark:hover:text-[#FF9B4A] dark:focus-visible:outline-primary"
                  type="button"
                >
                  <X className="size-5" />
                </button>
              </SheetClose>
            </div>

            <nav
              aria-label={labels.mobileNavigation}
              className="flex flex-1 flex-col gap-1 py-6"
            >
              {navigationItems.map((item) => {
                const ariaCurrent = getNavigationItemCurrent(item.href)
                return (
                  <SheetClose asChild key={item.href}>
                    <a
                      aria-current={ariaCurrent}
                      className={`rounded-xl border-l-2 px-3 py-3 text-base font-semibold text-foreground transition-colors hover:bg-muted hover:text-[#A94300] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#A94300] dark:hover:text-[#FF9B4A] dark:focus-visible:outline-primary ${
                        ariaCurrent
                          ? 'border-primary bg-primary/[0.08]'
                          : 'border-transparent'
                      }`}
                      href={item.href}
                      onClick={() =>
                        trackNavigation(item.trackingValue, 'mobile')
                      }
                    >
                      {item.label}
                    </a>
                  </SheetClose>
                )
              })}
            </nav>

            <div className="grid gap-3 border-t border-border pt-5">
              {isAuthenticated === false && (
                <>
                  <SheetClose asChild>
                    <Link
                      className="inline-flex min-h-11 items-center justify-center rounded-xl border border-border px-5 text-sm font-semibold text-foreground transition-colors hover:border-primary/60"
                      onClick={() =>
                        trackEvent('landing_cta_click', {
                          type: 'login',
                          placement: 'mobile',
                        })
                      }
                      search={{ redirect: '/chat' }}
                      to="/login"
                    >
                      {labels.login}
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      className="inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-md transition-transform hover:brightness-110 active:scale-[0.98]"
                      onClick={() => trackChatCta('mobile')}
                      to="/chat"
                    >
                      {labels.startFree}
                    </Link>
                  </SheetClose>
                </>
              )}
              {isAuthenticated === true && (
                <SheetClose asChild>
                  <Link
                    className="inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-md transition-transform hover:brightness-110 active:scale-[0.98]"
                    onClick={() => trackChatCta('mobile')}
                    to="/chat"
                  >
                    {labels.openIqdoc}
                  </Link>
                </SheetClose>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}
